import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Textarea } from '@rebass/forms';
import { Input, Label, Select } from '../Form';
import { gql, useMutation } from '@apollo/client';
import { formatISO } from 'date-fns';
import useNotify from '../../hooks/useNotify';
import { assemblySizes } from './options';
import useMakesAndModels from '../../hooks/useMakesAndModels';
import BackflowAssemblyMakePicker from './BackflowAssemblyMakePicker';
import BackflowAssemblyModelPicker from './BackflowAssemblyModelPicker';
import FormRow from '../Form/FormRow';
import FormColumn from '../Form/FormColumn';
import Form from '../Form/Form';
import TagPicker from '../TagPicker/TagPicker';
import { AssemblyTypeKey } from '@ecosconnect/core/src/types/jobFormData';
import { AssemblyProtectionTypePicker } from './AssemblyProtectionPicker';
import { AssemblyTypePicker } from './AssemblyTypePicker';

type ModalColumnProps = {
  onClose: (assemblyId?: string) => void;
} & (
  | {
      action: 'create';
      assembly?: Partial<AssemblyInput>;
    }
  | {
      action: 'edit';
      assembly: AssemblyInput;
    }
  | {
      action: 'replace';
      assembly: AssemblyInput;
    }
);

const CREATE_BACKFLOW_ASSEMBLY = gql`
  mutation CreateBackflowAssembly($input: BackflowAssemblyInput!) {
    createBackflowAssembly(input: $input) {
      id
      waterServiceId
      addressId
      modelId
      model {
        id
        name
      }
      replacesAssemblyId
      waterSupplierId
      location
      size
      serialNumber
      type
      testAnniversary
      lastTestDate
      hazardType
      hazardLevel
      installDate
      orientation
      tags
    }
  }
`;

const UPDATE_BACKFLOW_ASSEMBLY = gql`
  mutation UpdateBackflowAssembly($id: ID!, $input: BackflowAssemblyInput!) {
    updateBackflowAssembly(id: $id, input: $input) {
      id
      waterServiceId
      addressId
      modelId
      model {
        id
        name
      }
      replacesAssemblyId
      replacesAssembly {
        id
        status
      }
      waterSupplierId
      location
      size
      serialNumber
      type
      testAnniversary
      lastTestDate
      hazardType
      hazardLevel
      protectionType
      installDate
      orientation
      tags
    }
  }
`;

type AssemblyInput = {
  id: string;
  modelId?: string;
  addressId: string;
  waterServiceId?: string;
  waterSupplierId: string;
  replacesAssemblyId?: string;
  type: AssemblyTypeKey;
  size?: number;
  serialNumber: string;
  location: string;
  testAnniversary: string;
  hazardLevel?: 'LOW' | 'HIGH';
  hazardType: string;
  protectionType?: 'SERVICE' | 'INTERNAL';
  tags: string[];
  installDate?: string;
};

const AssemblyEditModal: React.FC<ModalColumnProps> = ({
  onClose,
  assembly,
  action,
}) => {
  const [selectedMakeId, setSelectedMakeId] = useState<string>();

  const today = formatISO(new Date(), { representation: 'date' });

  const { showError, showSuccess } = useNotify();

  const { makesByModel } = useMakesAndModels();

  const [assemblyInput, setAssemblyInput] = useState<Partial<AssemblyInput>>(
    action === 'replace' && assembly
      ? {
          modelId: '',
          addressId: assembly.addressId,
          waterServiceId: assembly.waterServiceId,
          waterSupplierId: assembly.waterSupplierId,
          replacesAssemblyId: assembly.id,
          type: undefined,
          size: assembly.size,
          serialNumber: '',
          location: assembly.location,
          testAnniversary: assembly.testAnniversary,
          hazardLevel: assembly.hazardLevel,
          hazardType: assembly.hazardType,
          protectionType: assembly.protectionType,
          tags: assembly.tags ?? [],
          installDate: '',
        }
      : action === 'edit' && assembly
        ? {
            modelId: assembly.modelId,
            addressId: assembly.addressId,
            waterServiceId: assembly.waterServiceId,
            waterSupplierId: assembly.waterSupplierId,
            type: assembly.type,
            size: assembly.size,
            serialNumber: assembly.serialNumber,
            location: assembly.location,
            testAnniversary: assembly.testAnniversary,
            hazardLevel: assembly.hazardLevel,
            hazardType: assembly.hazardType,
            protectionType: assembly.protectionType,
            tags: assembly.tags ?? [],
            installDate: assembly.installDate,
          }
        : {
            modelId: assembly?.modelId ?? '',
            addressId: assembly?.addressId ?? '',
            waterServiceId: assembly?.waterServiceId ?? '',
            waterSupplierId: assembly?.waterSupplierId ?? '',
            type: assembly?.type ?? undefined,
            size: assembly?.size ?? undefined,
            serialNumber: assembly?.serialNumber ?? '',
            location: assembly?.location ?? '',
            testAnniversary: assembly?.testAnniversary ?? today,
            hazardLevel: assembly?.hazardLevel ?? undefined,
            hazardType: assembly?.hazardType ?? '',
            protectionType: assembly?.protectionType,
            tags: assembly?.tags ?? [],
            installDate: assembly?.installDate,
          }
  );

  const handleTagChange = (tags: readonly string[]) => {
    setAssemblyInput((prevState) => ({
      ...prevState,
      tags: tags.slice(),
    }));
  };

  const onInputChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setAssemblyInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  }, []);

  const onNumericInputChange = useCallback((e: any) => {
    const { name, value } = e.target;

    let number: number | undefined = parseFloat(value);
    if (isNaN(number)) {
      number = undefined;
    }

    setAssemblyInput((prevState) => ({
      ...prevState,
      [name]: number,
    }));
  }, []);

  const [createBackflowAssembly, { loading: createLoading }] = useMutation(
    CREATE_BACKFLOW_ASSEMBLY,
    {
      onError: (error) => {
        showError('Error creating backflow assembly', error);
        console.error('Error creating backflow assembly', error);
      },
    }
  );

  const [updateBackflowAssembly, { loading: updateLoading }] = useMutation(
    UPDATE_BACKFLOW_ASSEMBLY,
    {
      onError: (error) => {
        showError('Error updating backflow assembly', error);
        console.error('Error updating backflow assembly', error);
      },
    }
  );

  const save = useCallback(async () => {
    switch (action) {
      case 'create':
        await createBackflowAssembly({ variables: { input: assemblyInput } })
          .then(async (result) => {
            if (result.data?.createBackflowAssembly) {
              showSuccess('New assembly was successfully saved');
              onClose(result.data.createBackflowAssembly.id);
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
      case 'edit':
      case 'replace':
        await updateBackflowAssembly({
          variables: { id: assembly?.id, input: assemblyInput },
        })
          .then(async (result) => {
            if (result.data?.updateBackflowAssembly) {
              // Do something with data here?
              showSuccess('The assembly was successfully updated!');
              onClose(result.data.updateBackflowAssembly.id);
            }
          })
          .catch((error) => {
            showError('Something went wrong', error);
            console.error(error.errors);
          });
        break;
    }
  }, [
    action,
    assembly?.id,
    assemblyInput,
    createBackflowAssembly,
    updateBackflowAssembly,
  ]);

  useEffect(() => {
    if (
      makesByModel &&
      assemblyInput.modelId &&
      makesByModel[assemblyInput.modelId]
    ) {
      setSelectedMakeId(makesByModel[assemblyInput.modelId].id);
    }
  }, [makesByModel, assemblyInput.modelId, setSelectedMakeId]);

  return (
    <Modal
      title={
        action === 'edit'
          ? 'Edit Assembly'
          : action === 'replace'
            ? 'Replace Assembly'
            : 'Add Assembly'
      }
      onClose={() => onClose()}
    >
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <Label>Make</Label>
              <BackflowAssemblyMakePicker
                makeId={selectedMakeId}
                placeholder=""
                onChange={(selected) => {
                  setSelectedMakeId(selected?.value);
                }}
              />
            </FormColumn>
            <FormColumn>
              {selectedMakeId && (
                <>
                  <Label>Model</Label>
                  <BackflowAssemblyModelPicker
                    makeId={selectedMakeId}
                    modelId={assemblyInput.modelId}
                    placeholder=""
                    onChange={(value) => {
                      setAssemblyInput((prevState) => ({
                        ...prevState,
                        modelId: value?.value,
                      }));
                    }}
                  />
                </>
              )}
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Type</Label>
              <AssemblyTypePicker
                value={assemblyInput.type}
                onChange={(value) => {
                  setAssemblyInput((prevState) => ({
                    ...prevState,
                    type: value,
                  }));
                }}
              />
            </FormColumn>
            <FormColumn>
              <Label>Size</Label>
              <Select
                name="size"
                onChange={onNumericInputChange}
                value={assemblyInput.size}
                width="100%"
              >
                {!assemblyInput.size && <option value={undefined}></option>}
                {assemblySizes.map((size, i) => (
                  <option value={size.decimal} key={i}>
                    {size.fraction}
                  </option>
                ))}
              </Select>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Serial Number</Label>
              <Input
                name="serialNumber"
                onChange={onInputChange}
                value={assemblyInput.serialNumber}
                width="100%"
                autoComplete="off"
              />
            </FormColumn>
            <FormColumn>
              <Label>Protection Type</Label>
              <AssemblyProtectionTypePicker
                value={assemblyInput.protectionType}
                isClearable
                placeholder=""
                onChange={(value) => {
                  setAssemblyInput((prevState) => ({
                    ...prevState,
                    protectionType: value,
                  }));
                }}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Hazard Type</Label>
              <Input
                name="hazardType"
                onChange={onInputChange}
                value={assemblyInput.hazardType}
                width="100%"
              />
            </FormColumn>
            <FormColumn>
              <Label>Hazard Level</Label>
              <Select
                name="hazardLevel"
                onChange={onInputChange}
                value={assemblyInput.hazardLevel}
                width="100%"
              >
                <option value={undefined}></option>
                <option value="LOW">Low</option>
                <option value="HIGH">High</option>
              </Select>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Test Anniversary</Label>
              <Input
                name="testAnniversary"
                onChange={onInputChange}
                type="date"
                value={assemblyInput.testAnniversary}
                autoComplete="off"
              />
            </FormColumn>
            <FormColumn>
              <Label>Install Date</Label>
              <Input
                name="installDate"
                onChange={onInputChange}
                type="date"
                value={assemblyInput.installDate}
                autoComplete="off"
              />
            </FormColumn>
          </FormRow>
          <FormColumn>
            <Label>Location</Label>
            <Textarea
              value={assemblyInput.location}
              name="location"
              onChange={onInputChange}
            />
          </FormColumn>
          <FormRow>
            <FormColumn>
              <Label>Tags</Label>
              <TagPicker
                value={assemblyInput.tags}
                onChange={handleTagChange}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="save"
          onClick={save}
          disabled={createLoading || updateLoading}
        >
          Save
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={() => onClose()}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssemblyEditModal.propTypes = {
  onClose: PropTypes.any,
  assembly: PropTypes.any,
  action: PropTypes.any.isRequired,
};

export default AssemblyEditModal;
